import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import { Contact } from "@/services/backend/contacts/types/contact";
import { useState } from "react";
import {
  RtkQueryMutation,
  useDebouncedMutation,
} from "@/shared/lib/debounce/debounce";
import { FieldErrors } from "@/shared/lib/errors/validation-error";
import { joinFieldName } from "@/shared/lib/errors/err-validation-failed-context";
import { TextInputField } from "@/shared/components/field/input-field";

export function ContactCard({
  contact,
  onContactChange,
  title = t("Kontakt"),
  description = t("Kontakt beim Kunden."),
  fieldErrors: ownFieldErrors = undefined,
  fieldName = "contact",
  disabled = false,
}: {
  contact: Contact;
  onContactChange: (contact: Contact) => void;
  title?: string;
  description?: string;
  /**
   * Additional errors, keyed relative to the contact (e.g. "email"),
   * merged with the errors of the ErrValidationFailedContext.
   */
  fieldErrors?: FieldErrors;
  /**
   * The field name of the contact in the ErrValidationFailedContext. Its fields
   * are read from and reset at `${fieldName}.<field>`, e.g. "contact.email".
   */
  fieldName?: string;
  disabled?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
        <div className="flex items-start space-x-4">
          <a href={`mailto:${contact.email}`} rel="noreferrer">
            <Button variant="outline">{t("E-Mail")}</Button>
          </a>
          <a href={`tel:${contact.mobilephone}`} rel="noreferrer">
            <Button variant="outline">{t("Anrufen")}</Button>
          </a>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <TextInputField
          label={t("Name")}
          placeholder={t("Name")}
          text={contact.name}
          onTextChange={(name) => onContactChange({ ...contact, name })}
          fieldName={joinFieldName(fieldName, "name")}
          errors={ownFieldErrors?.name}
          disabled={disabled}
        />
        <TextInputField
          label={t("Mobil")}
          placeholder={t("Mobil")}
          text={contact.mobilephone}
          onTextChange={(mobilephone) =>
            onContactChange({ ...contact, mobilephone })
          }
          fieldName={joinFieldName(fieldName, "mobilephone")}
          errors={ownFieldErrors?.mobilephone}
          disabled={disabled}
        />
        <TextInputField
          label={t("E-Mail")}
          placeholder={t("E-Mail")}
          text={contact.email}
          onTextChange={(email) => onContactChange({ ...contact, email })}
          fieldName={joinFieldName(fieldName, "email")}
          errors={ownFieldErrors?.email}
          disabled={disabled}
        />
      </CardContent>
    </Card>
  );
}

type Mutation<Req, Res> = () => RtkQueryMutation<Req, Res>;

interface ContactCardWithMutationProps<Req, Res> {
  id: string;
  contact: Contact;
  mutation: Mutation<Req, Res>;
  title?: string;
  description?: string;
  /** See {@link ContactCard}. */
  fieldErrors?: FieldErrors;
  /** See {@link ContactCard}. Defaults to "contact". */
  fieldName?: string;
  disabled?: boolean;
}

/**
 * ContactCardWithMutation is a {@link ContactCard} saving the contact with a
 * PUT of `{ id, contact }`. Its validation errors are written into the
 * nearest ErrValidationFailedContextProvider, which the page mounts.
 */
export function ContactCardWithMutation<
  Req extends { id: string; contact: Contact },
  Res,
>({
  id,
  contact,
  mutation,
  title = t("Kontakt"),
  description = t("Kontakt beim Kunden."),
  fieldErrors = undefined,
  fieldName = "contact",
  disabled = false,
}: ContactCardWithMutationProps<Req, Res>) {
  // @ts-expect-error - I do not understand the error. But it works.
  const [request, setRequest] = useState<Req>({ id, contact });
  const debouncedMutation = mutation();
  useDebouncedMutation(
    request,
    setRequest,
    debouncedMutation,
    // @ts-expect-error - I do not understand the error. But it works.
    { id, contact },
  );

  return (
    <ContactCard
      contact={request.contact}
      // @ts-expect-error - I do not understand the error. But it works.
      onContactChange={(c) => setRequest({ id, contact: c })}
      title={title}
      description={description}
      fieldErrors={fieldErrors}
      fieldName={fieldName}
      disabled={disabled}
    />
  );
}

interface ServiceContactCardWithMutationProps<Req, Res> {
  id: string;
  serviceContact: Contact;
  mutation: Mutation<Req, Res>;
  title?: string;
  description?: string;
  /** See {@link ContactCard}. */
  fieldErrors?: FieldErrors;
  /** See {@link ContactCard}. Defaults to "serviceContact". */
  fieldName?: string;
  disabled?: boolean;
}

/**
 * Same as ContactCardWithMutation but mutation
 * is a patch for a serviceContact field.
 */
export function ServiceContactCardWithMutation<
  Req extends { id: string; serviceContact: Contact },
  Res,
>({
  id,
  serviceContact,
  mutation,
  title = t("Kontakt"),
  description = t("Kontakt beim Kunden."),
  fieldErrors = undefined,
  fieldName = "serviceContact",
  disabled = false,
}: ServiceContactCardWithMutationProps<Req, Res>) {
  // @ts-expect-error - I do not understand the error. But it works.
  const [request, setRequest] = useState<Req>({ id, serviceContact });
  const debouncedMutation = mutation();
  useDebouncedMutation(
    request,
    setRequest,
    debouncedMutation,
    // @ts-expect-error - I do not understand the error. But it works.
    { id, serviceContact },
  );

  return (
    <ContactCard
      contact={request.serviceContact}
      // @ts-expect-error - I do not understand the error. But it works.
      onContactChange={(c) => setRequest({ id, serviceContact: c })}
      title={title}
      description={description}
      fieldErrors={fieldErrors}
      fieldName={fieldName}
      disabled={disabled}
    />
  );
}
